import { unaryRpc } from "@redotech/rpc/definition";
import { yyyyMmDdSchema } from "@redotech/zod-util/db-coercion";
import * as z from "zod";

const MAX_AGENT_ACTIVITY_RANGE_DAYS = 366;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

function getUtcDay(date: string): number | undefined {
  const [year, month, day] = date.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    return undefined;
  }

  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return undefined;
  }
  return timestamp / MILLISECONDS_PER_DAY;
}

const calendarDateSchema = yyyyMmDdSchema.refine(
  (date) => getUtcDay(date) !== undefined,
  "Not a real calendar date",
);

const humanQueueAgentActivityInputSchema = z
  .object({ startDate: calendarDateSchema, endDate: calendarDateSchema })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must be on or before endDate",
    path: ["startDate"],
  })
  .refine(
    ({ startDate, endDate }) => {
      const startDay = getUtcDay(startDate);
      const endDay = getUtcDay(endDate);
      return (
        startDay === undefined ||
        endDay === undefined ||
        endDay - startDay < MAX_AGENT_ACTIVITY_RANGE_DAYS
      );
    },
    {
      message: `Date range cannot exceed ${MAX_AGENT_ACTIVITY_RANGE_DAYS} days.`,
      path: ["endDate"],
    },
  );

const humanQueueAgentActivityAgentSchema = z.object({
  adminUserId: z.string(),
  email: z.email(),
  totalSent: z.number().int().nonnegative(),
  defaultQueueSent: z.number().int().nonnegative(),
  defaultQueueModifiedSent: z.number().int().nonnegative(),
  flaggedQueueSent: z.number().int().nonnegative(),
  flaggedQueueModifiedSent: z.number().int().nonnegative(),
  modifiedSent: z.number().int().nonnegative(),
});

const humanQueueAgentDailySendsSchema = z.object({
  adminUserId: z.string(),
  date: yyyyMmDdSchema,
  sent: z.number().int().nonnegative(),
});

export const getHumanQueueAgentActivity = unaryRpc({
  name: "getHumanQueueAgentActivity" as const,
  description:
    "Get human queue send activity by admin user for an inclusive UTC date range.",
  input: humanQueueAgentActivityInputSchema,
  output: z.object({
    agents: z.array(humanQueueAgentActivityAgentSchema),
    dailySends: z.array(humanQueueAgentDailySendsSchema),
  }),
});

export type GetHumanQueueAgentActivityInput = z.infer<
  typeof humanQueueAgentActivityInputSchema
>;

export type GetHumanQueueAgentActivityOutput = z.infer<
  typeof getHumanQueueAgentActivity.output
>;
